import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Bell, Megaphone, Calendar, BarChart3, MessageSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const ICONS = {
  announcement: Megaphone,
  event: Calendar,
  poll: BarChart3,
  dm: MessageSquare,
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 12000);
    return () => clearInterval(timerRef.current);
  }, []);

  const onOpen = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try {
        await api.post("/notifications/read");
        setUnread(0);
        setItems((it) => it.map((x) => ({ ...x, read: true })));
      } catch (err) {
        console.error("Failed to mark notifications read:", err);
      }
    }
  };

  const go = (item) => {
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpen}>
      <DropdownMenuTrigger data-testid="notif-bell-btn" className="relative w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-center focus:outline-none">
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-coral text-white text-[11px] font-bold flex items-center justify-center" style={{ background: "#FF5757" }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-2xl p-0 overflow-hidden" data-testid="notif-dropdown">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h4 className="font-black font-display text-slate-900">Notifications</h4>
          <span className="text-xs text-slate-400">{items.length}</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No notifications yet</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.kind] || Bell;
            return (
              <button
                key={n.id}
                onClick={() => go(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 ${!n.read ? "bg-purple-50/60" : ""}`}
              >
                <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-800 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{n.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-purple-700 mt-2" />}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
